/* istanbul ignore file */
import { BlacReact, BlocObserver } from "blac";
import { Blac } from "blac-next";
import { getApiController } from "src/api/ApiController";
import AppVersionBloc from "src/hybrid/AppVersionBloc";
import { BiometricVerificationBloc } from "src/hybrid/components/BiometricVerification";
import KeyboardBloc from "src/hybrid/components/KeyboardControls";
import PushNotificationsBloc from "src/hybrid/components/PushNotificationsControls";
import StatusBarBloc from "src/hybrid/components/StatusBarBloc";
import { featureFlags } from "src/lib/featureFlags";
import { parseHubspotFingerprint } from "src/lib/hubspot";
import setupOpenApi from "src/lib/setupOpenApi";
import ApiMiddleware from "src/state/ApiMiddleware";
import DevicesCubit from "src/state/DevicesCubit/DevicesCubit";
import FilesCubit from "src/state/FilesCubit/FilesCubit";
import HistoryBloc from "src/state/HistoryBloc/HistoryBloc";
import ObservationBundleBloc from "src/state/ObservationBundleBloc/ObservationBundleBloc";
import OverlayCubit from "src/state/OverlayCubit/OverlayCubit";
import PatientPCPBloc from "src/state/PatientPCPBloc/PatientPCPBloc";
import PatientPharmacyBloc from "src/state/PatientPharmacyBloc/PatientPharmacyBloc";
import ProgramBloc from "src/state/ProgramBloc/ProgramBloc";
import SubscriptionCubit from "src/state/SubscriptionCubit/SubscriptionCubit";
import ToastBloc from "src/state/Toast/ToastBloc";
import TrackCubit, { setupMixpanel } from "src/state/Track/TrackCubit";
import VideoPlayerCubit from "src/state/VideoPlayerCubit/VideoPlayerCubit";
import HealthSyncBloc from "./HealthSyncBloc/HealthSyncBloc";
import RxDietCubit from "./RxDietCubit/RxDietCubit";
import AuthenticationBloc from "./UserCubit/AuthenticationBloc";
import RateCoachingCallCubit from "./RateCoachingCallCubit/RateCoachingCallCubit";
import UserCubit from "./UserCubit/UserCubit";
import UserEmailVerificationCubit from "./UserEmailVerificationCubit/UserEmailVerificationCubit";
import { StorageController } from "./StorageBloc/StorageBloc";

parseHubspotFingerprint(new URLSearchParams(window.location.search));
setupOpenApi();
setupMixpanel();

// Initialize StorageBloc FIRST before any other components
// This ensures storage is ready with user context from existing tokens
StorageController.initialize();

export const apiMiddleware = new ApiMiddleware();

// Hybrid App components
export const appVersionState = new AppVersionBloc();
export const pushNotificationState = Blac.getBloc(PushNotificationsBloc);
export const keyboardState = new KeyboardBloc();
export const biometricAuthState = new BiometricVerificationBloc();

export const authenticationState = Blac.getBloc(AuthenticationBloc);
getApiController({
  authenticationState,
  appVersionState
});
export const tracker = Blac.getBloc(TrackCubit);

export const overlayState = new OverlayCubit();
export const userState = new UserCubit();
export const subscriptionState = new SubscriptionCubit();
export const fileState = new FilesCubit();
export const programState = new ProgramBloc();
export const historyState = new HistoryBloc();
export const appStatusBarState = new StatusBarBloc();
export const patientPharmacyState = new PatientPharmacyBloc();
export const patientPCPState = new PatientPCPBloc();
export const devicesState = new DevicesCubit();
export const observationBundleState = new ObservationBundleBloc();
export const toast = new ToastBloc();
export const rxDietState = new RxDietCubit();
export const healthSyncState = new HealthSyncBloc();
export const rateCoachingCallState = new RateCoachingCallCubit();
export const userEmailVerificationState = new UserEmailVerificationCubit();

const debugObserver = new BlocObserver({
  onChange: (bloc, event) => {
    const debugModeActive =
      sessionStorage.getItem("debugModeActive") === "true";

    if (debugModeActive) {
      const blocName = bloc.constructor.name;

      console.log(`[BLAC].change: ${blocName}`, { bloc, event });
    }
  }
});

const state = new BlacReact(
  [
    healthSyncState,
    historyState,
    userState,
    fileState,
    subscriptionState,
    overlayState,
    keyboardState,
    biometricAuthState,
    programState,
    appStatusBarState,
    patientPharmacyState,
    patientPCPState,
    new VideoPlayerCubit(),
    featureFlags,
    devicesState,
    observationBundleState,
    toast,
    appVersionState,
    rxDietState
  ],
  {
    observer: debugObserver
  }
);

// eslint-disable-next-line @typescript-eslint/unbound-method
export const { useBloc, BlocProvider } = state;
export default state;
